import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import React from 'react';

import logoImage from 'src/assets/images/logos/logo-with-text_dark-148.png';

type TopId =
  | 'ambisjonsprosess'
  | 'lagorganisering'
  | 'prestasjonskultur'
  | 'ledelsePersonlig';

type MidId = 'now' | 'desired' | 'actions';

type SubId =
  | 'visjon'
  | 'misjon'
  | 'delmaal'
  | 'hundreAnsvar'
  | 'samspillsoner'
  | 'verdier'
  | 'feedback'
  | 'utfordringer'
  | 'coaching';

export type FormData = Record<MidId, Record<TopId, Record<SubId, string>>>;

const topDefs: Array<{
  id: TopId;
  title: string;
  subs: Array<{ id: SubId; title: string }>;
}> = [
  {
    id: 'ambisjonsprosess',
    title: 'Ambisjonsprosess',
    subs: [
      { id: 'visjon', title: 'Visjon' },
      { id: 'misjon', title: 'Misjon' },
      { id: 'delmaal', title: 'Delmål' },
    ],
  },
  {
    id: 'lagorganisering',
    title: 'Lagorganisering',
    subs: [
      { id: 'hundreAnsvar', title: '100% ansvar' },
      { id: 'samspillsoner', title: 'Samspillsoner (2-3 kritiske)' },
    ],
  },
  {
    id: 'prestasjonskultur',
    title: 'Prestasjonskultur',
    subs: [
      { id: 'verdier', title: 'Verdier' },
      { id: 'feedback', title: 'Feedback' },
    ],
  },
  {
    id: 'ledelsePersonlig',
    title: 'Ledelse/personlig utvikling',
    subs: [
      { id: 'utfordringer', title: 'Mine viktigste utfordringer' },
      { id: 'coaching', title: 'Coaching' },
    ],
  },
];

const styles = StyleSheet.create({
  page: {
    padding: 24,
    // Reserve space for fixed footer (logo + metadata).
    paddingBottom: 34,
    fontSize: 9,
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  title: { fontSize: 14, fontWeight: 700, color: '#111827' },
  meta: { color: '#6B7280', fontSize: 8 },

  table: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  row: { flexDirection: 'row', alignItems: 'stretch' },
  headerRow: { backgroundColor: '#F3F4F6' },
  sectionRow: {},

  cell: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  cellLast: { borderRightWidth: 0 },

  colLabel: { width: 170, flexGrow: 0, flexShrink: 0 },
  colNow: { width: 215, flexGrow: 0, flexShrink: 0 },
  colDesired: { width: 215, flexGrow: 0, flexShrink: 0 },
  colActions: { flex: 1, minWidth: 200 },

  th: { fontSize: 9, fontWeight: 700, color: '#111827' },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  label: { fontSize: 9, fontWeight: 700, color: '#111827' },
  value: {
    fontSize: 8.5,
    lineHeight: 1.25,
    color: '#374151',
    textAlign: 'left',
    width: '100%',
  },
  empty: { color: '#6B7280', fontStyle: 'italic' },

  footer: {
    position: 'absolute',
    left: 24,
    right: 24,
    bottom: 4,
    fontSize: 8,
    color: '#6B7280',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  footerLogoWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerRight: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  footerLogo: {
    width: 72,
    height: 20,
    objectFit: 'contain',
  },
});

const sectionBgColors: Record<TopId, string> = {
  ambisjonsprosess: '#E8EEF4',
  lagorganisering: '#E8F4EC',
  prestasjonskultur: '#F0E8F4',
  ledelsePersonlig: '#FBF0E6',
};

const formatDateTime = (d: Date) =>
  `${d.toLocaleDateString('no-NO')} ${d.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const safeText = (v?: string) => (v ?? '').trim();

const clamp = (v: string, maxLen: number) => {
  const t = v.replace(/\s+/g, ' ').trim();
  if (!t) {
    return { text: '—', isEmpty: true };
  }
  if (t.length <= maxLen) {
    return { text: t, isEmpty: false };
  }
  return { text: `${t.slice(0, Math.max(0, maxLen - 1))}…`, isEmpty: false };
};

export const DevelopmentPlanPdf: React.FC<{
  data: FormData;
  exportedAt?: Date;
  /** Brukernavn for tittel, f.eks. "Ola" → "Olas utviklingsplan". */
  userName?: string | null;
}> = ({ data, exportedAt = new Date(), userName }) => {
  const name = userName?.trim() ?? '';
  const title =
    name.length > 0
      ? `${name}${/s$/i.test(name) ? "'" : 's'} utviklingsplan`
      : 'Min utviklingsplan';

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.meta}>
            Eksportert {formatDateTime(exportedAt)}
          </Text>
        </View>

        <View style={styles.table}>
          <View style={[styles.row, styles.headerRow]}>
            <View style={[styles.cell, styles.colLabel]}>
              <Text style={styles.th}></Text>
            </View>
            <View style={[styles.cell, styles.colNow]}>
              <Text style={styles.th}>Nå-tilstand</Text>
            </View>
            <View style={[styles.cell, styles.colDesired]}>
              <Text style={styles.th}>Ønsket tilstand</Text>
            </View>
            <View style={[styles.cell, styles.colActions, styles.cellLast]}>
              <Text style={styles.th}>Tiltak</Text>
            </View>
          </View>

          {topDefs.flatMap((top) => {
            const sectionHeader = (
              <View
                key={`${top.id}-section`}
                style={[
                  styles.row,
                  styles.sectionRow,
                  { backgroundColor: sectionBgColors[top.id] },
                ]}
              >
                <View style={[styles.cell, styles.cellLast, { flexGrow: 1 }]}>
                  <Text style={styles.sectionTitle}>{top.title}</Text>
                </View>
              </View>
            );

            const rows = top.subs.map((sub) => {
              const now = clamp(safeText(data?.now?.[top.id]?.[sub.id]), 220);
              const desired = clamp(
                safeText(data?.desired?.[top.id]?.[sub.id]),
                220
              );
              const actions = clamp(
                safeText(data?.actions?.[top.id]?.[sub.id]),
                260
              );

              return (
                <View key={`${top.id}-${sub.id}`} style={styles.row}>
                  <View style={[styles.cell, styles.colLabel]}>
                    <Text style={styles.label}>{sub.title}</Text>
                  </View>
                  <View style={[styles.cell, styles.colNow]}>
                    <Text
                      style={[styles.value, now.isEmpty ? styles.empty : {}]}
                    >
                      {now.text}
                    </Text>
                  </View>
                  <View style={[styles.cell, styles.colDesired]}>
                    <Text
                      style={[
                        styles.value,
                        desired.isEmpty ? styles.empty : {},
                      ]}
                    >
                      {desired.text}
                    </Text>
                  </View>
                  <View
                    style={[styles.cell, styles.colActions, styles.cellLast]}
                  >
                    <Text
                      style={[
                        styles.value,
                        actions.isEmpty ? styles.empty : {},
                      ]}
                    >
                      {actions.text}
                    </Text>
                  </View>
                </View>
              );
            });

            return [sectionHeader, ...rows];
          })}
        </View>

        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <Text>Oppsummering</Text>
          </View>
          <View style={styles.footerLogoWrap}>
            <Image src={logoImage} style={styles.footerLogo} />
          </View>
          <View style={styles.footerRight}>
            <Text>Eksportert {formatDateTime(exportedAt)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
